import React from "react";
import { Check, MapPin, Users } from "lucide-react";

/**
 * One choosable exam centre.
 *
 * Rendered as a radio rather than a plain panel: choosing a centre is a
 * required, mutually exclusive decision, and the previous version marked the
 * chosen one with a one-pixel ring that was easy to miss entirely — candidates
 * reached a disabled Submit button without realising they had never picked one.
 * The selected state is now unmissable: a filled tick, an accent border, a
 * tinted background and the word "Selected".
 */
export default function CenterCard({
  name,
  state,
  address,
  capacity,
  selected,
  onClick,
}: {
  name: string;
  state?: string | null;
  address?: string | null;
  capacity?: number | null;
  selected?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={[
        "w-full text-left border-2 rounded-xl p-4 transition-all",
        selected
          ? "border-indigo-600 bg-indigo-50/60 shadow-sm"
          : "border-gray-200 hover:border-gray-400 hover:bg-gray-50",
      ].join(" ")}
    >
      <div className="flex items-start gap-3">
        {/* The radio itself. Present whether or not it is chosen, so the whole
            list reads as "pick one" at a glance. */}
        <span
          className={[
            "mt-0.5 shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center",
            selected ? "border-indigo-600 bg-indigo-600" : "border-gray-300 bg-white",
          ].join(" ")}
        >
          {selected ? <Check className="w-3 h-3 text-white" strokeWidth={3} /> : null}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`font-semibold ${selected ? "text-indigo-900" : "text-gray-900"}`}>
              {name}
            </span>
            {selected ? (
              <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-indigo-600 text-white">
                Selected
              </span>
            ) : null}
          </div>

          <div className="text-xs text-gray-500 mt-1 flex items-start gap-1.5">
            <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
            <span className="min-w-0">
              {[state, address].filter(Boolean).join(" • ") || "—"}
            </span>
          </div>
        </div>

        {capacity ? (
          <span className="shrink-0 inline-flex items-center gap-1 text-xs rounded-full bg-white border border-gray-200 px-2 py-1 text-gray-600">
            <Users className="w-3 h-3" />
            {capacity}
          </span>
        ) : null}
      </div>
    </button>
  );
}
